/**
 * Keeps catalog/gallery quick-add buttons in sync with cart quantities,
 * and refreshes the header cart bubble / notification after add or remove.
 */
if (!customElements.get('ac-quick-add')) {
  let cartFetch = null;
  let refreshTimer = null;
  let uiRefreshTimer = null;

  class AcQuickAdd extends HTMLElement {
    connectedCallback() {
      this.qtyEl = this.querySelector('[data-ac-qa-qty]');
      this.btn = this.querySelector('.ac-qa__btn--add');
      this.minusBtn = this.querySelector('[data-ac-qa-minus]');
      this.modalOpener = this.querySelector('modal-opener');
      this.productId = Number(this.dataset.productId);
      this.setQty(Number(this.dataset.cartQty) || 0);

      this._onMinus = this.onMinus.bind(this);
      this._onAddClick = this.onAddClick.bind(this);
      this._onDocPointerDown = this.onDocPointerDown.bind(this);
      this._onModalClosed = this.onModalClosed.bind(this);
      this._onPointerEnter = this.onPointerEnter.bind(this);
      this._onPointerLeave = this.onPointerLeave.bind(this);
      this.minusBtn?.addEventListener('click', this._onMinus);
      /* Capture: first tap on qty expands; second tap handles plus */
      this.btn?.addEventListener('click', this._onAddClick, true);
      document.addEventListener('pointerdown', this._onDocPointerDown, true);
      document.body.addEventListener('modalClosed', this._onModalClosed);
      this.addEventListener('pointerenter', this._onPointerEnter);
      this.addEventListener('pointerleave', this._onPointerLeave);

      this._unsub =
        typeof subscribe === 'function'
          ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              if (event?.source !== 'ac-quick-add') {
                AcQuickAdd.scheduleQtyRefresh();
              }
              AcQuickAdd.scheduleCartUIRefresh();
            })
          : null;
    }

    disconnectedCallback() {
      this.minusBtn?.removeEventListener('click', this._onMinus);
      this.btn?.removeEventListener('click', this._onAddClick, true);
      document.removeEventListener('pointerdown', this._onDocPointerDown, true);
      document.body.removeEventListener('modalClosed', this._onModalClosed);
      this.removeEventListener('pointerenter', this._onPointerEnter);
      this.removeEventListener('pointerleave', this._onPointerLeave);
      if (this._unsub) this._unsub();
    }

    isHoverDevice() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    onPointerEnter(event) {
      if (event.pointerType === 'touch') return;
      if (!this.isHoverDevice()) return;
      if (!this.classList.contains('ac-qa--in-cart')) return;
      /* Clear post-add lock on a fresh hover so +/- show on first enter */
      this.unlock();
      this.classList.add('ac-qa--expanded');
    }

    onPointerLeave(event) {
      /* Touch fires pointerleave before click — collapsing here ate plus/minus taps */
      if (event.pointerType === 'touch') return;
      if (!this.isHoverDevice()) return;
      if (this.classList.contains('ac-qa--locked')) {
        this.unlock();
        return;
      }
      this.collapse();
    }

    onDocPointerDown(event) {
      if (!this.classList.contains('ac-qa--expanded') && !this.classList.contains('ac-qa--locked')) return;
      if (this.contains(event.target)) return;
      this.unlock();
      this.collapse();
    }

    /** After product popup closes (add or dismiss), rest on the qty circle */
    onModalClosed() {
      const modalId = this.modalOpener?.getAttribute('data-modal');
      if (!modalId) return;
      const modal = document.querySelector(modalId);
      if (modal?.hasAttribute('open')) return;
      this.unlock();
      this.collapse();
      AcQuickAdd.scheduleQtyRefresh();
    }

    collapse() {
      this.classList.remove('ac-qa--expanded');
      const active = document.activeElement;
      if (active && this.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }
    }

    /** Force number/cart UI even while finger/cursor still sits on the control */
    lockRest() {
      this.classList.remove('ac-qa--expanded');
      this.classList.add('ac-qa--locked');
      const active = document.activeElement;
      if (active && this.contains(active) && typeof active.blur === 'function') {
        active.blur();
      }
    }

    unlock() {
      this.classList.remove('ac-qa--locked');
    }

    /**
     * In-cart: first tap on qty circle expands +/-.
     * Plus (already expanded): instantly rest to number/cart, then open popup or add.
     */
    onAddClick(event) {
      if (!this.classList.contains('ac-qa--in-cart')) return;

      if (!this.classList.contains('ac-qa--expanded')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.unlock();
        this.classList.add('ac-qa--expanded');
        /* Focus only on desktop — mobile focus + pointerleave desynced +/- from state */
        if (this.isHoverDevice()) {
          this.btn?.focus({ preventScroll: true });
        }
        return;
      }

      /* Plus while expanded — rest on number/cart, then open popup or add */
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.lockRest();

      if (this.modalOpener) {
        const selector = this.modalOpener.getAttribute('data-modal');
        const modal = selector ? document.querySelector(selector) : null;
        if (modal && typeof modal.show === 'function') {
          modal.show(this.btn);
        }
        return;
      }

      /* Single-variant: submit the product form programmatically */
      const form = this.querySelector('product-form form') || this.querySelector('form[data-type="add-to-cart-form"]');
      if (form?.requestSubmit) form.requestSubmit();
      else form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    setQty(qty) {
      const prev = Number(this.dataset.cartQty) || 0;
      const n = Math.max(0, Number(qty) || 0);
      this.dataset.cartQty = String(n);
      this.classList.toggle('ac-qa--in-cart', n > 0);
      if (n <= 0) this.classList.remove('ac-qa--expanded');

      if (this.qtyEl) {
        this.qtyEl.textContent = n > 0 ? String(n) : '';
        this.qtyEl.toggleAttribute('hidden', n <= 0);
      }

      if (this.minusBtn) {
        this.minusBtn.tabIndex = n > 0 ? 0 : -1;
        this.minusBtn.hidden = n <= 0;
      }

      if (this.btn) {
        const title = this.dataset.productTitle || '';
        const addLabel = this.dataset.addLabel || 'Add to cart';
        this.btn.setAttribute(
          'aria-label',
          n > 0 ? `${title}: ${n} in cart. ${addLabel}` : `${addLabel} — ${title}`
        );
      }

      /* After qty sync: rest on circle only while pointer is still on the control */
      if (!this._busy && prev !== n) {
        if (this.matches(':hover')) {
          this.lockRest();
        } else {
          this.unlock();
          this.collapse();
        }
      }
    }

    getCartUI() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    /** Minus: remove 1, then rest on qty circle / cart icon */
    async onMinus(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this._busy || Number(this.dataset.cartQty) <= 0) return;

      this._busy = true;
      this.classList.add('ac-qa--busy');
      this.lockRest();
      this.minusBtn?.setAttribute('aria-disabled', 'true');
      this.btn?.classList.add('loading');
      this.btn?.querySelector('.loading__spinner')?.classList.remove('hidden');

      try {
        const cart = await AcQuickAdd.fetchCart();
        /* Cart lines are oldest→newest; last matching line = last unit(s) added for this product */
        const item = [...(cart.items || [])]
          .reverse()
          .find((line) => Number(line.product_id) === this.productId);

        if (!item) {
          this.setQty(0);
          return;
        }

        const cartUI = this.getCartUI();
        const sectionIds = AcQuickAdd.sectionIdsFor(cartUI);
        const body = {
          id: item.key,
          quantity: Math.max(0, item.quantity - 1),
          sections: sectionIds,
          sections_url: window.location.pathname,
        };

        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        const response = await fetch(window.routes.cart_change_url, config).then((r) => r.json());
        if (response.status || response.errors) {
          throw new Error(response.description || response.message || response.errors);
        }

        if (response.sections) {
          AcQuickAdd.applySections(response.sections, cartUI);
        } else {
          await AcQuickAdd.refreshCartUI();
        }

        const qty = (response.items || [])
          .filter((line) => Number(line.product_id) === this.productId)
          .reduce((sum, line) => sum + (line.quantity || 0), 0);
        this.setQty(qty);

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ac-quick-add',
            cartData: response,
            productId: this.productId,
          });
        }

        AcQuickAdd.scheduleQtyRefresh();
      } catch (error) {
        console.error(error);
        await AcQuickAdd.refreshCartUI();
        AcQuickAdd.scheduleQtyRefresh();
      } finally {
        this._busy = false;
        this.classList.remove('ac-qa--busy');
        this.minusBtn?.removeAttribute('aria-disabled');
        this.btn?.classList.remove('loading');
        this.btn?.querySelector('.loading__spinner')?.classList.add('hidden');
        this.lockRest();
      }
    }

    static sectionIdsFor(cartUI) {
      if (!cartUI) return ['cart-icon-bubble'];
      if (cartUI.tagName === 'CART-NOTIFICATION') {
        return ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    static applySections(sections, cartUI) {
      if (!sections) return;

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        bubble.innerHTML = AcQuickAdd.sectionInnerHTML(sections['cart-icon-bubble']);
      }

      if (!cartUI) return;

      if (cartUI.tagName === 'CART-NOTIFICATION') {
        const button = document.getElementById('cart-notification-button');
        if (button && sections['cart-notification-button']) {
          button.innerHTML = AcQuickAdd.sectionInnerHTML(sections['cart-notification-button']);
        }

        // Product fragment is keyed by line; skip if selector misses — icon/button still update.
        const productMount = document.getElementById('cart-notification-product');
        if (productMount && sections['cart-notification-product']) {
          try {
            const html = sections['cart-notification-product'];
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const item = doc.querySelector('[id^="cart-notification-product-"]');
            productMount.innerHTML = item ? item.outerHTML : AcQuickAdd.sectionInnerHTML(html);
          } catch (e) {
            console.error(e);
          }
        }

        cartUI.classList.toggle('is-empty', false);
        return;
      }

      if (cartUI.tagName === 'CART-DRAWER' && sections['cart-drawer']) {
        const mount = document.getElementById('CartDrawer');
        if (mount) {
          mount.innerHTML = AcQuickAdd.sectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
        }
        const overlay = cartUI.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', cartUI.close.bind(cartUI));
        }
      }
    }

    static sectionInnerHTML(html, selector = '.shopify-section') {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector(selector) || doc.body;
      return node ? node.innerHTML : '';
    }

    static scheduleCartUIRefresh() {
      clearTimeout(uiRefreshTimer);
      uiRefreshTimer = setTimeout(() => {
        AcQuickAdd.refreshCartUI().catch(() => {});
      }, 50);
    }

    static scheduleQtyRefresh() {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => AcQuickAdd.refreshAllQty(), 80);
    }

    static async refreshCartUI() {
      const sectionIds = ['cart-icon-bubble', 'cart-notification-button'];
      if (document.querySelector('cart-drawer')) sectionIds.push('cart-drawer');

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?sections=${sectionIds.join(',')}`, { cache: 'no-store' });
      if (!response.ok) return;
      const sections = await response.json();
      AcQuickAdd.applySections(sections, document.querySelector('cart-notification') || document.querySelector('cart-drawer'));
    }

    static fetchCart() {
      if (!cartFetch) {
        cartFetch = fetch(`${window.routes?.cart_url || '/cart'}.js`, { cache: 'no-store' })
          .then((r) => r.json())
          .finally(() => {
            cartFetch = null;
          });
      }
      return cartFetch;
    }

    static refreshAllQty() {
      AcQuickAdd.fetchCart()
        .then((cart) => {
          const byProduct = Object.create(null);
          (cart.items || []).forEach((item) => {
            const id = item.product_id;
            byProduct[id] = (byProduct[id] || 0) + (item.quantity || 0);
          });

          document.querySelectorAll('ac-quick-add').forEach((el) => {
            el.setQty(byProduct[Number(el.dataset.productId)] || 0);
          });
        })
        .catch(() => {});
    }
  }

  customElements.define('ac-quick-add', AcQuickAdd);

  const syncCatalogWithCart = () => {
    AcQuickAdd.scheduleQtyRefresh();
    AcQuickAdd.scheduleCartUIRefresh();
  };

  /* Back from the cart often restores this page from memory, with the old quantities. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCatalogWithCart();
  });

  const navEntry = performance.getEntriesByType('navigation')[0];
  if (navEntry?.type === 'back_forward') syncCatalogWithCart();
}
